import React, { useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { IMediaItem, MediaType } from 'modules/media/interfaces/api';
import MenuAction from 'basic-ui-lib/components/MenuTabelAction';
import { ColumnsType } from 'antd/es/table';
import { Box, Colors, Ic3Dots, IcCopy, IcEdit, IcTrash, Table, Text } from 'basic-ui-lib';
import IcFolder from 'modules/media/assets/icons/IcFolder';
import ModalConfirm, { useModalConfirmRef } from 'widgets/ModalConfirm';
import { useDeleteMediaItem } from 'modules/media/services/hooks/useDeleteMediaItem';
import { MediaPreviewModal } from '../MediaPreviewModal';
import EditNameModal, { EditNameModalRef } from 'basic-ui-lib/components/Modal/EditNameModal';
import mediaApis from 'modules/media/services/apis';
import toast from 'utils/toast';
import useGetManageLibrary from 'modules/media/services/hooks/useGetManageLibrary';
import { RenderMedia } from 'basic-ui-lib/components/screen/RenderMedia';

interface MediaManagerListModeProps {
  items: IMediaItem[];
  onOpenFolder: (folder: IMediaItem) => void;
  currentPath: string;
}

const MediaManagerListMode = ({ items, onOpenFolder, currentPath }: MediaManagerListModeProps) => {
  const modalConfirmRef = useModalConfirmRef();
  const editNameModalRef = useRef<EditNameModalRef>(null);
  const [, mutate] = useGetManageLibrary(true);

  const { deleteItem } = useDeleteMediaItem({ currentPath });
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);

  const tableData = useMemo(() => items.map(({ children, ...rest }) => rest), [items]);
  const mediaItems = tableData.filter((i) => i.type !== 'FOLDER');

  const handlePreview = (path: string) => {
    setPreviewIndex(mediaItems.findIndex((i) => i.path === path));
  };

  const getListMenu = (record: IMediaItem) => [
    {
      key: 'edit_name',
      title: 'Edit Name',
      icon: <IcEdit width={16} height={16} />,
    },
    ...(record.type !== MediaType.FOLDER) ? [
    {
      key: 'copy_link',
      title: 'Copy Link',
      icon: <IcCopy width={16} height={16} />,
    },
    ] : [],
    {
      key: 'delete_media',
      title: 'Delete',
      icon: <IcTrash width={16} height={16} />,
    },
  ];

  const handleMenuClick = (item: any, record: IMediaItem) => {
    switch (item.key) {
      case 'edit_name':
        editNameModalRef.current?.open({ currentName: record.name, id: record.id, onFinish: handleEditName });
        break;
      case 'copy_link':
        navigator.clipboard.writeText(record.url || '');
        toast.success('Link copied to clipboard');
        break;
      case 'delete_media':
        handleDelete(record);
        break;
      default:
        break;
    }
  };

  const columns: ColumnsType<IMediaItem> = [
    {
      title: 'MEDIA',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Box alignItems='center' display='flex' gap={12}>
          {record.type === MediaType.FOLDER ? (
            <Box width={80} display='flex' justifyContent='flex-start' alignItems='center'>
              <IcFolder width={45} height={45} />
            </Box>
          ) : (
            <Box
              width={80}
              minWidth={80}
              height={45}
              borderRadius={4}
              backgroundColor={Colors.neutral.bg[4]}
              position='relative'
            >
              {/* {
                record.type === MediaType.IMAGE && record.url ? (
                  <img
                    src={record.url}
                    alt={record.name}
                    className='media-image'
                  />
                ) : record.type === MediaType.VIDEO && record.url ? (
                  <video
                    src={record.url}
                    className='media-video'
                    loop
                    muted
                  />
                ) : (null)
              } */}
              {RenderMedia({ url: record.url || '', type: record.type, name: record.name}, 4, false, false)}
            </Box>
          )}
          <Text content_2 medium color='primary.6' ellipsis lineClamp={2}>
            {text}
          </Text>
        </Box>
      ),
      width: 500,
    },
    {
      title: 'TYPE',
      dataIndex: 'type',
      key: 'type',
    },
    {
      title: 'SIZE',
      dataIndex: 'size',
      key: 'size',
    },
    {
      title: 'DATE MODIFIED',
      dataIndex: 'dateModified',
      key: 'dateModified',
    },
    {
      title: 'ACTIONS',
      key: 'action',
      width: 100,
      align: 'center',
      render: (_, record) => (
        <div onClick={(e) => e.stopPropagation()} style={{ cursor: 'pointer' }}>
          <MenuAction listMenu={getListMenu(record)} onClick={(item) => handleMenuClick(item, record)}>
            <Ic3Dots width={20} height={20} />
          </MenuAction>
        </div>
      ),
    },
  ];

  const onClickRow = (record: IMediaItem) => {
    return {
      onClick: () => {
        if (record.type !== MediaType.FOLDER) {
          handlePreview(record.path);
        }
      },
      onDoubleClick: () => {
        if (record.type === MediaType.FOLDER) {
          onOpenFolder(record);
        }
      }
    };
  };

  const handleDelete = (item: IMediaItem) => {
    modalConfirmRef.current?.open(() => deleteItem(item), {
      message: `Do you want to delete this ${item.type.toLowerCase()}?`,
    });
  };

  const handleEditName = async (id: string, newName: string) => {
    await mediaApis.renameMediaItem(id, newName);
    toast.success('Name updated successfully');
    mutate(currentPath);      
  };

  return (
    <Container>
      <Table<IMediaItem> columns={columns} dataSource={tableData} rowKey='id' pagination={false} onRow={onClickRow} />
      <ModalConfirm ref={modalConfirmRef} />
      {previewIndex !== null && (
        <MediaPreviewModal
          items={mediaItems}
          initialIndex={previewIndex}
          onClose={() => setPreviewIndex(null)}
          setPreviewIndex={setPreviewIndex}
        />
      )}
      <EditNameModal ref={editNameModalRef} />
    </Container>
  );
};

export default MediaManagerListMode;

const Container = styled.div`
  width: 100%;

  .media-image, .media-video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 4px;
  }

  .fzt-table .ant-table-tbody > tr:hover {
    cursor: pointer;
  }
`;
